import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { ConnectorType } from '../data/live';
import { Empty, Loading, Pill } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/**
 * The couplers this network uses.
 *
 * The list used to be written out three times — the driver app, its vehicle
 * sheet, and the console's connector editor — so adding one meant an app
 * release and the three could disagree until every phone had updated. It lives
 * in the database now, and this is where it is changed.
 *
 * `code` is what vehicles and bays already store and is never editable:
 * changing it would orphan every one of them. `label` is what a person reads
 * and may change freely. Retiring takes a type out of what is offered while
 * leaving anything that already names it readable.
 */
export function ConnectorTypes() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['admin-connector-types'],
    queryFn: () => live.allConnectorTypes(),
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ['admin-connector-types'] });
    // The apps and the other console screens read the served list.
    qc.invalidateQueries({ queryKey: ['connector-types'] });
  };

  const add = useMutation({
    mutationFn: (v: { code: string; label: string; position: number }) =>
      live.addConnectorType(v),
    onSuccess: () => {
      done();
      setAdding(false);
    },
    onError: (e: Error) => setFailed(errorText(e)),
  });

  const edit = useMutation({
    mutationFn: (v: {
      code: string;
      patch: { label?: string; position?: number; active?: boolean };
    }) => live.editConnectorType(v.code, v.patch),
    onSuccess: done,
    onError: (e: Error) => setFailed(errorText(e)),
  });

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="li-sub">
          Offered when a driver adds a car and when you set up a bay. The order
          here is the order they appear in.
        </div>
        <button className="btn sm" onClick={() => { setFailed(null); setAdding(true); }}>
          Add a type
        </button>
      </div>

      {failed && <div className="note" role="alert" style={{ marginBottom: 10, color: 'var(--bad)' }}>{failed}</div>}

      {isLoading ? (
        <Loading />
      ) : !data ? (
        <Empty title="Connector types are not available." hint={loadFailedHint(loadError)} />
      ) : !data.length ? (
        <Empty title="No connector types" hint="Add the couplers this network uses." />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th>Order</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <Row
                  key={t.code}
                  type={t}
                  pending={edit.isPending}
                  onSave={(patch) => edit.mutate({ code: t.code, patch })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddType
          pending={add.isPending}
          nextPosition={((data?.at(-1)?.position ?? 0) + 10)}
          onClose={() => setAdding(false)}
          onSubmit={(v) => add.mutate(v)}
        />
      )}
    </>
  );
}

function Row({
  type,
  pending,
  onSave,
}: {
  type: ConnectorType;
  pending: boolean;
  onSave: (patch: { label?: string; position?: number; active?: boolean }) => void;
}) {
  const [label, setLabel] = useState(type.label);
  const [position, setPosition] = useState(String(type.position));
  const dirty = label !== type.label || position !== String(type.position);

  return (
    <tr>
      {/* Not editable: this is what every vehicle and bay already stores. */}
      <td className="mono">{type.code}</td>
      <td>
        <input
          className="field"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{ maxWidth: 200 }}
        />
      </td>
      <td>
        <input
          className="field"
          value={position}
          inputMode="numeric"
          onChange={(e) => setPosition(e.target.value.replace(/\D/g, ''))}
          style={{ maxWidth: 80 }}
        />
      </td>
      <td>
        <Pill tone={type.active ? 'on' : 'off'}>
          {type.active ? 'offered' : 'retired'}
        </Pill>
      </td>
      <td className="act">
        {dirty && (
          <button
            className="btn sm"
            disabled={pending || !label.trim()}
            onClick={() =>
              onSave({ label: label.trim(), position: Number(position) || 100 })
            }
          >
            Save
          </button>
        )}
        <button
          className="btn sm ghost"
          disabled={pending}
          title={
            type.active
              ? 'Stops it being offered. Cars and bays already set to it keep reading as it.'
              : 'Offers it again.'
          }
          onClick={() => onSave({ active: !type.active })}
        >
          {type.active ? 'Retire' : 'Offer again'}
        </button>
      </td>
    </tr>
  );
}

function AddType({
  pending,
  nextPosition,
  onClose,
  onSubmit,
}: {
  pending: boolean;
  nextPosition: number;
  onClose: () => void;
  onSubmit: (v: { code: string; label: string; position: number }) => void;
}) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');

  return (
    <div className="drawer">
      <div className="drawer-body">
        <h3>Add a connector type</h3>

        <label className="lbl">Code</label>
        <input
          className="inp"
          autoFocus
          value={code}
          placeholder="CCS2"
          onChange={(e) => setCode(e.target.value)}
        />
        <div className="li-sub" style={{ marginTop: 4, marginBottom: 10 }}>
          What gets stored on a vehicle and on a bay. It cannot be changed
          afterwards, because everything that names it would be orphaned.
        </div>

        <label className="lbl">Label</label>
        <input
          className="inp"
          value={label}
          placeholder="Leave empty to use the code"
          onChange={(e) => setLabel(e.target.value)}
        />
        <div className="li-sub" style={{ marginTop: 4, marginBottom: 10 }}>
          What a driver reads. This one can change.
        </div>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="btn"
            disabled={pending || code.trim().length < 2}
            onClick={() =>
              onSubmit({
                code: code.trim(),
                label: label.trim() || code.trim(),
                position: nextPosition,
              })
            }
          >
            {pending ? 'Adding…' : 'Add'}
          </button>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
